import { useTranslation } from "@next-platform/client-common-core";
import { forwardRef, useCallback, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";

import useUnsavedChangesGuard from "@/hooks/useUnsavedChangesGuard";

import DiscardChangesModalV2 from "./DiscardChangesModalV2";
import ReactRouterPrompt from "./ReactRouterPrompt";

export type UnsavedChangesGuardHandle = {
  handleCancel: () => void;
  markSaveNavigationAsSafe: () => void;
  resetSaveNavigationBypass: () => void;
};

type UnsavedChangesGuardProps = {
  hasUnsavedChanges: boolean;
  isSubmitting?: boolean;
  onDiscard?: () => void;
  heading?: string;
  description?: string;
};

function DiscardChangesModal({
  description,
  heading,
  isActive,
  onCancel,
  onConfirm,
}: {
  description: string;
  heading: string;
  isActive: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!isActive) {
    return null;
  }

  return <DiscardChangesModalV2 description={description} heading={heading} handleClose={onCancel} isVisible={true} onUserConfirmation={onConfirm} />;
}

const UnsavedChangesGuard = forwardRef<UnsavedChangesGuardHandle, UnsavedChangesGuardProps>(function UnsavedChangesGuard(
  { hasUnsavedChanges, isSubmitting = false, onDiscard, heading, description },
  ref,
) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleDiscard = useCallback(() => {
    if (onDiscard) {
      onDiscard();
      return;
    }
    navigate(-1);
  }, [navigate, onDiscard]);

  const resolvedHeading = heading ?? t("common:modal.unsavedChanges.title");
  const resolvedDescription = description ?? t("common:modal.unsavedChanges.message");

  const {
    handleCancel,
    handleCloseDiscardModal,
    handleConfirmDiscard,
    markSaveNavigationAsSafe,
    resetSaveNavigationBypass,
    shouldBlockNavigation,
    stateIsDiscardModalVisible,
  } = useUnsavedChangesGuard({
    hasUnsavedChanges,
    isSubmitting,
    onDiscard: handleDiscard,
  });

  useImperativeHandle(ref, () => ({ handleCancel, markSaveNavigationAsSafe, resetSaveNavigationBypass }), [
    handleCancel,
    markSaveNavigationAsSafe,
    resetSaveNavigationBypass,
  ]);

  return (
    <div data-testid="UnsavedChangesGuard">
      <ReactRouterPrompt when={shouldBlockNavigation}>
        {({ isActive, onConfirm, onCancel: onCancelNav }: { isActive: boolean; onConfirm: () => void; onCancel: () => void }) => (
          <DiscardChangesModal
            description={resolvedDescription}
            heading={resolvedHeading}
            isActive={isActive}
            onCancel={onCancelNav}
            onConfirm={onConfirm}
          />
        )}
      </ReactRouterPrompt>

      <DiscardChangesModal
        description={resolvedDescription}
        heading={resolvedHeading}
        isActive={stateIsDiscardModalVisible}
        onCancel={handleCloseDiscardModal}
        onConfirm={handleConfirmDiscard}
      />
    </div>
  );
});

export default UnsavedChangesGuard;
